<template>
    <div class="mk-event-row" @click="$emit('metricClicked', event.systemName)">
        <div class="event-label">
            <span class="gap"></span>
            <span class="range-metric-title">{{ $translate(event.systemName) }}</span>
            <annotation-hazard :mk-event="event" :is-duc="isDuc" />
        </div>

        <div class="range-metric-value" :class="{ error: isOutOfRange }">
            {{ $uiUtils.formatNumber(value, 1) }}
        </div>

        <div class="range-metric-range-container minimal">
            <div class="delta-value" :class="deltaColor">
                {{ $uiUtils.formatNumber(delta, 0) }}
            </div>
        </div>

        <div class="range-metric-range-container extended">
            <div class="range-metric-element">
                <div
                    v-for="(range, index) in coloredRange"
                    :key="index"
                    :class="$uiUtils.getEventRangeColor(range.color)"
                    class="graphical-event-tick"
                    :style="`left: ${
                        ((index > 0 ? coloredRange[index - 1].time : 0) / maxColoredRangeTime) * 100
                    }%;
             width: ${
                 ((range.time - (index > 0 ? coloredRange[index - 1].time : 0)) /
                     maxColoredRangeTime) *
                 100
             }%;`"
                ></div>

                <div
                    class="marker"
                    :class="deltaColor"
                    :style="`left: calc( ${markerLeft}%  - 0.2rem) `"
                >
                    <div class="pointer" :class="deltaColor">
                        <span>{{ $uiUtils.formatNumber(delta, 0) }}</span>
                    </div>

                    <div class="chubchik" :class="deltaColor"></div>
                </div>
            </div>

            <div class="axes">
                <div class="mr-auto">0</div>
                <div>{{ $uiUtils.formatNumber(maxColoredRangeTime, 0) }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import AnnotationHazard from '../common/AnnotationHazard'

export default {
    name: 'RangeMetric',
    components: { AnnotationHazard },
    props: {
        event: {
            type: Object,
            required: true
        },
        isDuc: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        value() {
            return this.event.value
        },
        isOutOfRange() {
            const { min, max } = this.event

            return this.value < min || this.value > max
        },
        coloredRange() {
            return this.event.range
        },
        maxColoredRangeTime() {
            if (!this.event.range) {
                console.error('NO RANGE', this.event)
                return
            }

            return this.event.range.last().time
        },
        delta() {
            return this.event.delta
        },
        deltaColor() {
            if (!this.event.range) {
                console.error('NO RANGE', this.event)
                return
            }

            return this.$uiUtils.getColoredRangeMetricColor(this.event)
        },
        markerLeft() {
            return (
                ((this.delta <= this.maxColoredRangeTime ? this.delta : this.maxColoredRangeTime) /
                    this.maxColoredRangeTime) *
                100
            )
        }
    }
}
</script>

<style lang="scss" scoped>
.mk-event-row {
    width: 100%;
    display: grid;
    column-gap: 0.5rem;
    color: $light;
    padding: 0.5rem 1rem;

    @media screen and #{$new-break4close} {
        grid-template-columns: 3fr 3fr 2fr;
    }
    @media screen and #{$new-break5open} {
        grid-template-columns: 2fr 3fr 5fr;
    }

    .event-label {
        display: grid;
        grid-template-columns: 1fr 2fr 2fr;

        .range-metric-title {
            display: flex;
            align-items: center;
            color: $light-3 !important;
        }
    }

    .range-metric-value {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 0.25rem;
        color: $light-3;
        border-radius: $border-radius-sm;

        &.error {
            color: $red;
        }
    }
    .range-metric-range-container {
        &.minimal {
            @media screen and #{$new-break4close} {
                display: flex;
                justify-content: center;

                .delta-value {
                    margin: 0;
                    padding: 0.25rem;
                    text-align: center;
                    color: $dark-4;
                    border-radius: $border-radius;

                    &.red {
                        color: $red;
                    }
                    &.amber {
                        color: $orange;
                    }
                    &.green {
                        color: $green;
                    }
                }
            }
            @media screen and #{$new-break5open} {
                display: none;
            }
        }
        &.extended {
            padding: 0 0.5rem;

            @media screen and #{$new-break4close} {
                display: none;
            }
            @media screen and #{$new-break5open} {
                .range-metric-element {
                    position: relative;
                    width: 100%;
                    height: 0.4rem;
                    margin-top: 0.5rem;
                    background: transparent;

                    .graphical-event-tick {
                        position: absolute;
                        height: 0.4rem;
                        opacity: 0.25;
                    }

                    .marker {
                        height: 1rem;
                        width: 1.2rem;
                        display: flex;
                        flex-direction: column;
                        position: absolute;
                        bottom: 0.4rem;
                        border-radius: 0.1rem;
                        box-shadow: 0 0 0.3rem rgba(0, 0, 0, 0.5);

                        &.red {
                            background-color: $red;
                        }
                        &.amber {
                            background-color: $orange;
                        }
                        &.green {
                            background-color: $green;
                        }

                        .pointer {
                            width: 100%;
                            height: 100%;
                            display: flex;
                            align-items: center;
                            justify-content: center;

                            span {
                                font-size: 8pt;
                                color: $dark-4;
                                font-weight: var(--font-weight-bold);
                            }
                        }
                        .chubchik {
                            height: 0;
                            border-width: 0.3rem;
                            border-left: 0.6rem solid transparent;
                            border-right: 0.6rem solid transparent;

                            &.red {
                                border-top: 0.3rem solid $red;
                            }
                            &.amber {
                                border-top: 0.3rem solid $orange;
                            }
                            &.green {
                                border-top: 0.3rem solid $green;
                            }
                        }
                    }
                }

                .axes {
                    display: flex;
                    font-size: 7pt;
                }
            }
        }
    }
}
</style>
